import { Box, LinearProgress, Link as MUILink, Stack, Typography } from "@mui/joy";
import { Outlet, useLocation } from "react-router";

import { motion, useReducedMotion } from "framer-motion";
import { useContext, useEffect, useRef, useState } from "react";
import Sidebar from "../components/ui/Sidebar";
import BottomMenu from "../components/ui/BottomMenu";
import { LayoutContext } from "../context";
import AdSlot from "../components/Ads/user/AdSlot";
import { APIGetUserTariffInfo } from "../api";
import { saveLastRoute } from "../lastRoute";

// Оформление кабинета: фон, карточки, кнопки, чаты, анимации.
// Подключается один раз здесь — дальше экраны просто ставят классы lvc-*.
import "../components/ui/lovirds-cabinet.css";


/**
 * Каркас приложения.
 *
 * Что поправлено:
 *  - на телефоне сверху висела пустая полоса ~96px: это был отступ под мобильную
 *    шапку, а саму шапку когда-то убрали, отступ остался;
 *  - высота контента считалась через calc(... - var(--Header-height) - 94px),
 *    а переменная --Header-height в проекте нигде не задаётся, поэтому браузер
 *    выбрасывал всё правило height целиком и высота «плавала» от экрана к экрану.
 *    Теперь обычная флекс-колонка: контент занимает остаток и скроллится сам,
 *    тариф-бар, баннер и нижнее меню всегда прижаты к низу;
 *  - APIGetUserTariffInfo вызывался, но не был импортирован — загрузка тарифа
 *    молча падала в catch;
 *  - переход между экранами анимируется.
 */
export default function Layout() {
    const reduceMotion = useReducedMotion();
    const scrollContainerRef = useRef(null); // Референс на блок для прокрутки
    const location = useLocation();
    const { setTariffModalOpen, token, currentUser, systemSettings } = useContext(LayoutContext);
    const [tariffInfo, setTariffInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    // Загружаем информацию о тарифе
    useEffect(() => {
        const loadTariffInfo = async () => {
            if (token && currentUser?.tariff_info) {
                setTariffInfo(currentUser.tariff_info);
                setLoading(false);
            } else if (token) {
                try {
                    const data = await APIGetUserTariffInfo(token);
                    setTariffInfo(data.tariff_info);
                } catch (error) {
                    console.error('Ошибка загрузки информации о тарифе:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        loadTariffInfo();
    }, [token, currentUser]);

    // Запоминаем экран: при следующем входе вернём человека сюда же
    useEffect(() => {
        saveLastRoute(location.pathname);
    }, [location.pathname]);

    // Функция для отображения информации о тарифе
    const renderTariffInfo = () => {
        if (loading) return null;

        if (!systemSettings?.is_paid_mode) return null;

        // Если нет активного тарифа или базовый тариф
        if (!tariffInfo?.is_active || tariffInfo?.current_tariff === 'basic') {
            return (
                <Box sx={{
                    flex: 'none',
                    backgroundColor: 'warning.100',
                    borderTop: '1px solid',
                    borderColor: 'warning.300'
                }}>
                    <Stack direction={"row"} sx={{ px: 2, py: 1 }} justifyContent={'space-between'} alignItems="center">
                        <Box>
                            <Typography level="body-xs" sx={{ fontWeight: 700, color: "warning.700" }}>
                                У вас базовый тариф.
                            </Typography>
                            <Typography level="body-xs" sx={{ color: `warning.600` }}>
                                Доступно {tariffInfo?.likes_remaining || 5} лайков сег.
                            </Typography>
                        </Box>
                        <MUILink
                            level="body-xs"
                            sx={{ fontWeight: 700, color: "primary.500", cursor: 'pointer' }}
                            onClick={() => setTariffModalOpen(true)}
                        >
                            Улучшить
                        </MUILink>
                    </Stack>
                    <LinearProgress
                        determinate
                        thickness={3}
                        value={(tariffInfo?.likes_remaining || 5) * 20} // 5 лайков = 100%
                        color="warning"
                    />
                </Box>
            );
        }

        // Для платных тарифов
        const tariffNames = {
            'standard': 'Стандарт',
            'premium': 'PREMIUM',
            'vip': 'VIP'
        };

        const tariffColors = {
            'standard': 'primary',
            'premium': 'success',
            'vip': 'danger'
        };

        const tariffName = tariffNames[tariffInfo.current_tariff] || tariffInfo.current_tariff;
        const color = tariffColors[tariffInfo.current_tariff] || 'primary';

        return (
            <Box sx={{
                flex: 'none',
                backgroundColor: `${color}.50`,
                borderTop: '1px solid',
                borderColor: `${color}.200`
            }}>
                <Stack direction={"row"} sx={{ px: 2, py: 1 }} justifyContent={'space-between'} alignItems="center">
                    <Box>
                        <Typography level="body-sm" sx={{ fontWeight: 700, color: `${color}.700` }}>
                            Тариф "{tariffName}"
                        </Typography>
                        <Typography level="body-xs" sx={{ color: `${color}.600` }}>
                            Ост. {tariffInfo.remaining_days} дней • {tariffInfo.likes_remaining} лайков сег.
                        </Typography>
                    </Box>
                    <MUILink
                        level="body-xs"
                        sx={{ fontWeight: 700, color: `${color}.500`, cursor: 'pointer' }}
                        onClick={() => setTariffModalOpen(true)}
                    >
                        Управление
                    </MUILink>
                </Stack>
                <LinearProgress
                    determinate
                    thickness={3}
                    value={(tariffInfo.remaining_days / 30) * 100} // Прогресс относительно 30 дней
                    color={color}
                />
            </Box>
        );
    };

    return (
        <Box sx={{
            display: 'flex',
            height: '100dvh',
            width: '100%',
            maxWidth: '1000px',
            mx: 'auto',
            overflow: 'hidden',
            position: 'relative',
            boxSizing: 'border-box',
            backgroundColor: 'transparent',
            boxShadow: { xs: 'none', md: '0 0 64px rgba(96, 40, 160, 0.06)' },
        }}>
            <Sidebar/>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {/* Контент занимает всё оставшееся место и скроллится сам.
                    key по маршруту — анимация смены экрана */}
                <Box
                    component={motion.main}
                    key={location.pathname}
                    ref={scrollContainerRef}
                    className="Layout lvc-scroll"
                    initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: reduceMotion ? 0 : 0.26, ease: [0.22, 1, 0.36, 1] }}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                    }}
                >
                    <Outlet context={{ scrollContainerRef }}/>
                </Box>

                {/* Блок информации о тарифе */}
                {renderTariffInfo()}

                {/* Нижний баннер. deps на pathname — новое объявление
                    при каждом переходе между экранами */}
                <Box sx={{ flex: 'none' }}>
                    <AdSlot placement="banner_bottom" deps={[location.pathname]} />
                </Box>

                <BottomMenu/>
            </Box>
        </Box>
    );
}
